import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getLoginRedirect } from '../utils/loginRedirect';

import FormPassword from './FormPassword';
import Loading from './Loading';

function FormLogin() {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [errors, setErrors] = useState('');
    const [messages, setMessages] = useState(null);
    const location = useLocation();
    const redirectTo = getLoginRedirect(location.state?.from, '/painel/');

    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    useEffect(() => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            window.location.replace(redirectTo);
        }
    }, [redirectTo]);

    const baseUrl = process.env.REACT_APP_API_URL;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors('');

        if (!email || !senha) {
            setErrors('Por favor, preencha seu e-mail e senha.');
            setLoading(false);
            return;
        }

        const loginData = {
            email: email,
            password: senha,
            lembrar: rememberMe,
        };

        try {
            const response = await fetch(`${baseUrl}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(loginData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                setErrors(errorData.message || 'Usuário ou senha incorretos');
                return;
            }

            const data = await response.json();

            localStorage.setItem('access_token', data.token);
            localStorage.setItem('user_id', data.usuario.id);
            localStorage.setItem('user_type', data.usuario.funcao);

            window.location.replace(redirectTo);
        } catch (e) {
            setMessages(null);
            setErrors('Erro ao conectar com o servidor. Tente novamente.');
        } finally {
            setLoading(false);
            setMessages(null);
        }
    };

    const openPasswordModal = () => {
        setIsPasswordModalOpen(true);
    };

    const closePasswordModal = () => {
        setIsPasswordModalOpen(false);
    };

    useEffect(() => {
        if (location.state?.message) {
            setErrors(null);
            setMessages({
                type: location.state.message.type,
                message: location.state.message.text,
            });

            setTimeout(() => {
                setMessages(null);
            }, 6000);
        }
    }, [location.state]);

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="mb-2 ml-2 block text-sm text-white font-light">Email</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-lg border border-stroke py-3 lg:py-4 pl-6 pr-10 outline-none focus:border-secondary focus-visible:shadow-none"
                    />
                </div>

                <div className="mb-4">
                    <label className="mb-2 ml-2 block text-sm text-white font-light">Senha</label>
                    <input
                        type="password"
                        value={senha}
                        onChange={(e) => setSenha(e.target.value)}
                        className="w-full rounded-lg border border-stroke py-3 lg:py-4 pl-6 pr-10 outline-none focus:border-secondary focus-visible:shadow-none"
                    />
                </div>

                <div className="flex justify-between mb-8">
                    <label className="flex items-center mx-auto">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="relative aspect-square w-4 h-4 cursor-pointer appearance-none rounded-full border-2 border-secondary bg-transparent mr-1.5 after:absolute after:top-1/2 after:left-1/2 after:aspect-square after:h-3/4 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-transparent after:checked:bg-white checked:bg-none checked:text-white focus:ring-offset-0 focus:ring-0"
                        />
                        <span className="text-sm text-white font-light opacity-60">Lembrar de mim</span>
                    </label>
                </div>

                {errors && (
                    <div className="-mt-4 mb-6">
                        <div className="flex items-center justify-center w-full border-l-4 border-rose-500 bg-rose-100 px-7 py-4 shadow-md md:p-5">
                            <div className="mr-5 flex h-9 w-full max-w-[36px] items-center justify-center rounded-lg bg-rose-500">
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M6.4917 7.65579L11.106 12.2645C11.2545 12.4128 11.4715 12.5 11.6738 12.5C11.8762 12.5 12.0931 12.4128 12.2416 12.2645C12.5621 11.9445 12.5623 11.4317 12.2423 11.1114C12.2422 11.1113 12.2422 11.1113 12.2422 11.1113C12.242 11.1111 12.2418 11.1109 12.2416 11.1107L7.64539 6.50351L12.2589 1.91221L12.2595 1.91158C12.5802 1.59132 12.5802 1.07805 12.2595 0.757793C11.9393 0.437994 11.4268 0.437869 11.1064 0.757418C11.1063 0.757543 11.1062 0.757668 11.106 0.757793L6.49234 5.34931L1.89459 0.740581L1.89396 0.739942C1.57364 0.420019 1.0608 0.420019 0.740487 0.739944C0.42005 1.05999 0.419837 1.57279 0.73985 1.89309L6.4917 7.65579ZM6.4917 7.65579L1.89459 12.2639L1.89395 12.2645C1.74546 12.4128 1.52854 12.5 1.32616 12.5C1.12377 12.5 0.906853 12.4128 0.758361 12.2645L1.1117 11.9108L0.758358 12.2645C0.437984 11.9445 0.437708 11.4319 0.757539 11.1116C0.757812 11.1113 0.758086 11.111 0.75836 11.1107L5.33864 6.50287L0.740487 1.89373L6.4917 7.65579Z"
                                        fill="#ffffff"
                                        stroke="#ffffff"
                                    />
                                </svg>
                            </div>

                            <div className="w-full">
                                <h5 className="text-left text-rose-800 text-opacity-80 font-medium">{errors}</h5>
                            </div>
                        </div>
                    </div>
                )}

                {messages && (
                    <div className="-mt-4 mb-6">
                        <div
                            className={`flex items-center justify-center w-full border-l-4 px-7 py-4 shadow-md md:p-5 ${
                                messages.type === 'success' ? 'border-green-500 bg-green-100' : 'border-rose-500 bg-rose-100'
                            }`}
                        >
                            <div
                                className={`mr-5 flex h-9 w-full max-w-[36px] items-center justify-center rounded-lg ${
                                    messages.type === 'success' ? 'bg-green-500' : 'bg-rose-500'
                                }`}
                            >
                                {messages.type === 'success' ? (
                                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M15.2984 0.826822L15.2868 0.811827L15.2741 0.797751C14.9173 0.401867 14.3238 0.400754 13.9657 0.794406L5.91888 9.45376L2.05667 5.2868C1.69856 4.89287 1.10487 4.89389 0.747996 5.28987C0.417335 5.65675 0.417335 6.22337 0.747996 6.59026L0.747959 6.59029L0.752701 6.59541L4.86742 11.0348C5.14445 11.3405 5.52858 11.5 5.89581 11.5C6.29242 11.5 6.65178 11.3355 6.92401 11.035L15.2162 2.11161C15.5833 1.74452 15.576 1.18615 15.2984 0.826822Z"
                                            fill="white"
                                            stroke="white"
                                        />
                                    </svg>
                                ) : (
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M6.4917 7.65579L11.106 12.2645C11.2545 12.4128 11.4715 12.5 11.6738 12.5C11.8762 12.5 12.0931 12.4128 12.2416 12.2645C12.5621 11.9445 12.5623 11.4317 12.2423 11.1114C12.2422 11.1113 12.2422 11.1113 12.2422 11.1113C12.242 11.1111 12.2418 11.1109 12.2416 11.1107L7.64539 6.50351L12.2589 1.91221L12.2595 1.91158C12.5802 1.59132 12.5802 1.07805 12.2595 0.757793C11.9393 0.437994 11.4268 0.437869 11.1064 0.757418C11.1063 0.757543 11.1062 0.757668 11.106 0.757793L6.49234 5.34931L1.89459 0.740581L1.89396 0.739942C1.57364 0.420019 1.0608 0.420019 0.740487 0.739944C0.42005 1.05999 0.419837 1.57279 0.73985 1.89309L6.4917 7.65579ZM6.4917 7.65579L1.89459 12.2639L1.89395 12.2645C1.74546 12.4128 1.52854 12.5 1.32616 12.5C1.12377 12.5 0.906853 12.4128 0.758361 12.2645L1.1117 11.9108L0.758358 12.2645C0.437984 11.9445 0.437708 11.4319 0.757539 11.1116C0.757812 11.1113 0.758086 11.111 0.75836 11.1107L5.33864 6.50287L0.740487 1.89373L6.4917 7.65579Z"
                                            fill="#ffffff"
                                            stroke="#ffffff"
                                        />
                                    </svg>
                                )}
                            </div>

                            <div className="w-full">
                                <h5
                                    className={`text-left font-medium ${
                                        messages.type === 'success' ? 'text-green-800 text-opacity-80' : 'text-rose-800 text-opacity-80'
                                    }`}
                                >
                                    {messages.message}
                                </h5>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-8">
                    <button className="w-3/4 max-w-40 cursor-pointer rounded-lg border-2 border-secondary bg-secondary bg-opacity-0 p-2 lg:text-xl text-white font-bold uppercase transition hover:bg-opacity-100">
                        {!loading ? 'Entrar' : <Loading className="text-black fill-secondary" />}
                    </button>
                </div>

                <div className="mb-5">
                    <button
                        type="button"
                        className="text-xs text-white underline opacity-60 transition hover:opacity-75"
                        onClick={openPasswordModal}
                    >
                        Esqueceu sua senha?
                    </button>
                </div>
            </form>

            {isPasswordModalOpen && <FormPassword closeModal={closePasswordModal} />}
        </>
    );
}

export default FormLogin;
